import { Point } from '@/types/common';
import { ScaleData } from '@/types/floorPlanViewer';

/**
 * Convert pixels to real-world units
 */
export function pixelsToUnits(pixels: number, scaleData: ScaleData | null): number {
  if (!scaleData) return pixels;
  return pixels / scaleData.pixelsPerUnit;
}

/**
 * Convert real-world units to pixels
 */
export function unitsToPixels(units: number, scaleData: ScaleData | null): number {
  if (!scaleData) return units;
  return units * scaleData.pixelsPerUnit;
}

/**
 * Format a measurement for display
 */
export function formatMeasurement(pixels: number, scaleData: ScaleData | null): string {
  if (!scaleData) return `${pixels.toFixed(0)}px`;
  
  const units = pixelsToUnits(pixels, scaleData);
  
  // Special formatting for feet - show as ft'-in"
  if (scaleData.unit === 'ft') {
    const totalInches = units * 12;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    
    // If inches are 12, add to feet
    if (inches === 12) {
      return `${feet + 1}'-0"`;
    }
    
    return `${feet}'-${inches}"`;
  }
  
  const precision = getUnitPrecision(scaleData.unit);
  return `${units.toFixed(precision)} ${scaleData.unit}`;
}

/**
 * Get appropriate decimal precision for a unit
 */
function getUnitPrecision(unit: string): number {
  switch (unit) {
    case 'mm':
      return 0; // No decimals for millimeters
    case 'cm':
      return 1; // One decimal for centimeters
    case 'm':
      return 2; // Two decimals for meters
    case 'in':
      return 1; // One decimal for inches
    case 'ft':
      return 2; // Two decimals for feet
    default:
      return 2;
  }
}

/**
 * Calculate distance between two points
 */
export function calculateDistance(point1: Point, point2: Point): number {
  return Math.sqrt(
    Math.pow(point2.x - point1.x, 2) + 
    Math.pow(point2.y - point1.y, 2)
  );
}

/**
 * Calculate the area of a polygon given its points
 */
export function calculatePolygonArea(points: Point[]): number {
  if (points.length < 3) return 0;
  
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  
  return Math.abs(area) / 2;
}

/**
 * Format an area measurement
 */
export function formatArea(pixelArea: number, scaleData: ScaleData | null): string {
  if (!scaleData) return `${pixelArea.toFixed(0)}px²`;
  
  const unitArea = pixelArea / Math.pow(scaleData.pixelsPerUnit, 2);
  
  // For feet, show as "sq ft" instead of "ft²"
  if (scaleData.unit === 'ft') {
    return `${unitArea.toFixed(2)} sq ft`;
  }
  
  const precision = getUnitPrecision(scaleData.unit);
  return `${unitArea.toFixed(precision)} ${scaleData.unit}²`;
}

/**
 * Round a number to a "nice" value for display
 */
export function roundToNiceNumber(value: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  
  let nice;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  
  return nice * magnitude;
}

/**
 * Normalize unit string from DWG to our standard format
 */
export function normalizeUnit(dwgUnit: string): 'mm' | 'cm' | 'm' | 'in' | 'ft' {
  const unit = dwgUnit.toLowerCase();
  
  if (unit.includes('millimeter') || unit === 'mm') return 'mm';
  if (unit.includes('centimeter') || unit === 'cm') return 'cm';
  if (unit.includes('meter') || unit === 'm') return 'm';
  if (unit.includes('inch') || unit === 'in' || unit === '"') return 'in';
  if (unit.includes('feet') || unit.includes('foot') || unit === 'ft' || unit === "'") return 'ft';
  
  // Default to meters if unknown
  return 'm';
}

/**
 * Calculate display scale (e.g., "1:100")
 */
export function calculateDisplayScale(pixelWidth: number, realWidth: number, unit: string): string {
  // Convert to a common unit (mm) for scale calculation
  let widthInMM = realWidth;
  
  switch (unit) {
    case 'cm':
      widthInMM = realWidth * 10;
      break;
    case 'm':
      widthInMM = realWidth * 1000;
      break;
    case 'in':
      widthInMM = realWidth * 25.4;
      break;
    case 'ft':
      widthInMM = realWidth * 304.8;
      break;
  }
  
  // Assuming screen pixels at 96 DPI (standard web)
  const screenWidthMM = (pixelWidth / 96) * 25.4;
  const scale = Math.round(widthInMM / screenWidthMM);
  
  return `1:${scale}`;
}

/**
 * Snap a point to grid based on real-world units
 */
export function snapToGrid(point: Point, gridSize: number, scaleData: ScaleData | null): Point {
  if (!scaleData) return point;
  
  // Grid size is in real units (e.g., 1 foot)
  const gridPixels = gridSize * scaleData.pixelsPerUnit;
  
  return {
    x: Math.round(point.x / gridPixels) * gridPixels,
    y: Math.round(point.y / gridPixels) * gridPixels
  };
}

/**
 * Calculate dimensions for each wall of a polygon
 */
export function calculateWallDimensions(points: Point[], scaleData: ScaleData | null) {
  if (!points || points.length < 2) return [];
  
  return points.map((point, i) => {
    const nextPoint = points[(i + 1) % points.length];
    const distance = calculateDistance(point, nextPoint);
    
    // Calculate midpoint for label placement
    const midpoint = {
      x: (point.x + nextPoint.x) / 2,
      y: (point.y + nextPoint.y) / 2
    };
    
    // Calculate angle for text rotation
    const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x);
    const degrees = angle * (180 / Math.PI);
    
    // Normalize angle to be readable (flip if upside down)
    let textAngle = degrees;
    if (degrees > 90 || degrees < -90) {
      textAngle = degrees + 180;
    }
    
    return {
      start: point,
      end: nextPoint,
      midpoint,
      distance,
      angle: degrees,
      textAngle,
      formatted: formatMeasurement(distance, scaleData)
    };
  });
}

/**
 * Calculate the perimeter of a polygon
 */
export function calculatePolygonPerimeter(points: Point[]): number {
  if (points.length < 2) return 0;
  
  let perimeter = 0;
  for (let i = 0; i < points.length; i++) {
    const nextIndex = (i + 1) % points.length;
    perimeter += calculateDistance(points[i], points[nextIndex]);
  }
  
  return perimeter;
}

/**
 * Get optimal position for area label inside polygon
 */
export function getAreaLabelPosition(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  
  // For now, use centroid - can be enhanced with pole of inaccessibility
  const centroid = {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length
  };
  
  return centroid;
}

/**
 * Calculate offset position for dimension line
 */
export function calculateDimensionOffset(start: Point, end: Point, offset: number): {
  start: Point;
  end: Point;
} {
  // Calculate perpendicular direction
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  if (length === 0) return { start, end };
  
  // Normalize and get perpendicular
  const perpX = -dy / length;
  const perpY = dx / length;
  
  return {
    start: {
      x: start.x + perpX * offset,
      y: start.y + perpY * offset
    },
    end: {
      x: end.x + perpX * offset,
      y: end.y + perpY * offset
    }
  };
}